<template>
  <form-component>
    <form
      id="account"
      data-vv-scope="account"
      novalidate="novalidate"
      @submit.prevent="onSubmit"
    >
      <div class="form-row">
        <div class="col-12 col-md-4">
          <input-text
            id="firstName"
            v-model="firstName"
            v-validate="'required'"
            name="firstName"
            data-vv-name="firstName"
            data-vv-as="first name"
            label="First Name"
            type="text"
            required
            :error="errors.first('account.firstName')"
          />
        </div>
        <div class="col-12 col-md-4">
          <input-text
            id="lastName"
            v-model="lastName"
            v-validate="'required'"
            name="lastName"
            data-vv-name="lastName"
            data-vv-as="last name"
            label="Last Name"
            type="text"
            required
            :error="errors.first('account.lastName')"
          />
        </div>
        <div class="col-12 col-md-4">
          <input-text
            id="phoneNumber"
            v-model="phoneNumber"
            name="phoneNumber"
            data-vv-name="phoneNumber"
            data-vv-as="phone number"
            label="Cell Phone"
            type="tel"
            tooltip="This will be used for optional text message notifications"
            :error="errors.first('account.phoneNumber')"
          />
        </div>
      </div>
      <div class="form-row">
        <div class="col-12 col-xl-6">
          <birthday-picker v-model="dateOfBirth" />
        </div>
        <div class="col-12 col-xl-6">
          <location-picker v-model="currentLocation" />
        </div>
      </div>
      <div class="form-row">
        <div class="col-12 col-xl-6">
          <gender-select
            id="gender"
            v-model="gender"
            v-validate="'required'"
            name="gender"
            data-vv-name="gender"
            data-vv-as="gender"
            required
            :select-options="counselorGenderOptions"
            label="WHAT IS YOUR GENDER IDENTITY?"
            :error="errors.first('account.gender')"
          />
        </div>
        <div class="col-12 col-xl-6">
          <counselor-ethnicity-select
            id="ethnicity"
            v-model="ethnicity"
            v-validate="'required'"
            name="ethnicity"
            data-vv-name="ethnicity"
            data-vv-as="ethnicity"
            required
            label="WHAT IS YOUR ETHNICITY?"
            :error="errors.first('account.ethnicity')"
          />
        </div>
        <div class="col-12 col-xl-6">
          <lgbtq-picker v-model="lgbtq" />
        </div>
        <div class="col-12 col-xl-6">
          <religion-picker v-model="religion" />
        </div>
        <div class="col-12">
          <label class="replacement-label">
            What languages are you fluent in besides English?
          </label>
          <language-picker v-model="languages" data-tags="languages" />
        </div>
        <div class="col-12">
          <input-text-area
            id="biography"
            v-model="biography"
            v-validate="'required'"
            name="biography"
            data-vv-name="biography"
            data-vv-as="biography"
            label="Biography"
            required
            :error="errors.first('account.biography')"
            type="text"
          />
        </div>
      </div>

      <button
        type="submit"
        :disabled="saving"
        class="btn btn-primary btn-wide mt-4"
      >
        <font-awesome-icon
          v-if="saving"
          :spin="true"
          :icon="['fas', 'spinner']"
          class="icon alt"
        />
        <span v-else>Save Changes</span>
      </button>
    </form>
  </form-component>
</template>

<script>
import UserClient from '@/api/UserClient';
import FormComponent from '@/components/FormComponent.vue';
import CounselorEthnicitySelect from '../components/CounselorEthnicitySelect.vue';
import GenderSelect from '@/components/inputs/GenderSelect.vue';
import InputText from '@/components/inputs/InputText.vue';
import InputTextArea from '@/components/inputs/InputTextArea.vue';
import { tagFromName } from '@/helpers/tags';
import BirthdayPicker from '../components/BirthdayPicker.vue';
import LanguagePicker from '../components/LanguagePicker.vue';
import LocationPicker from '../components/LocationPicker.vue';
import LgbtqPicker from '../components/LgbtqPicker.vue';
import ReligionPicker from '../components/ReligionPicker.vue';
import moment from 'moment';
import genderOptions from '@/helpers/mixins/gender-options';

export default {
  name: 'PersonalInfo',
  components: {
    FormComponent,
    CounselorEthnicitySelect,
    GenderSelect,
    InputText,
    InputTextArea,
    BirthdayPicker,
    LanguagePicker,
    LocationPicker,
    LgbtqPicker,
    ReligionPicker,
  },
  mixins: [genderOptions],

  data() {
    return {
      userClient: new UserClient(this.$http),
      saving: false,

      firstName: '',
      lastName: '',
      dateOfBirth: {
        year: '',
        month: '',
        day: ''
      },
      currentLocation: null,
      gender: '',
      ethnicity: '',
      lgbtq: '',
      religion: '',
      phoneNumber: '',
      languages: [],
      biography: '',
    };
  },

  async mounted() {
    const user = this.$store.getters.currentUser;

    this.firstName = user.first_name;
    this.lastName = user.last_name || '';
    this.dateOfBirth = { year: '', month: '', day: '' };
    this.phoneNumber = (user.phone_number || '').replace(/^\+1/, '');

    const dob = user.date_of_birth
      ? user.date_of_birth.split('-')
      : [];
    if (dob.length === 3) {
      this.dateOfBirth = {
        year: parseInt(dob[0], 10).toString(),
        month: parseInt(dob[1], 10).toString(),
        day: parseInt(dob[2], 10).toString()
      };
    }

    this.currentLocation = user.city;
    this.gender = (user.gender || '').toLowerCase();
    this.ethnicity = user.race || '';

    const counselor = user.counselor_profile;
    if (!counselor) {
      this.$store.commit('setGlobalAlert', {
        msg: 'There was an error loading this page. Please try again!',
        error: true
      });
      console.error('user.counselor_profile was falsy!', user);
      return;
    }

    this.lgbtq = counselor.lgbtq === null ? '' : counselor.lgbtq.toString();
    this.religion = counselor.religion || '';
    this.languages = counselor.languages.map(tagFromName);
    this.biography = counselor.biography || '';
  },

  methods: {
    async onSubmit() {
      const result = await this.$validator.validateAll('account');
      if (!result) {
        this.$store.commit('setGlobalAlert', {
          msg: "Please make sure you've filled out all of the required fields!",
          error: true
        });
        return;
      }

      const phoneNumber = this.phoneNumber.replace(/[^\d.]/g, '');
      if (phoneNumber.length > 0 && phoneNumber.length !== 10) {
        this.$store.commit('setGlobalAlert', {
            msg: 'Please enter your full cell phone number. For example, (585) 123-1234',
            error: true
          });
        return;
      }

      try {
        this.errors.clear();

        this.saving = true;

        const dobMoment = moment({
          ...this.dateOfBirth,
          month: this.dateOfBirth.month - 1
        });
        const dobStr = dobMoment.format('YYYY-MM-DD');

        await this.userClient.updateUserAccount(
          this.firstName,
          this.lastName,
          dobStr,
          phoneNumber ? `+1${phoneNumber}` : '',
          false,
          this.gender,
          this.ethnicity,
          this.currentLocation ? this.currentLocation.id : null,
          this.biography
        );

        const data = {
          lgbtq: this.lgbtq === '' ? null : this.lgbtq,
          religion: this.religion === '' ? null : this.religion,
          language_ids: this.languages.map((s) => s.id),
          biography: this.biography,
        };
        await this.userClient.saveCounselorProfile(data);

        const user = await this.userClient.getRemoteUser();
        this.$store.commit('setUser', user);

        this.$store.commit('setGlobalAlert', {
          msg: 'Profile Updated!'
        });
      } catch (ex) {
        console.error(ex);
        this.$store.commit('setGlobalAlert', {
          msg: 'Failed to save account. Please try again!',
          error: true
        });
      } finally {
        this.saving = false;
      }
    },
  }
};
</script>

<style lang="scss" scoped>
.account {
  .profile-button {
    @media (min-width: $md-min) {
      min-width: 150px;
    }
  }

  label {
    @include label;

    font-size: 12px;
  }

  h2 {
    margin-top: 10px;
    font-size: 28px;
    margin-bottom: 30px;
  }

  .replacement-label {
    text-transform: uppercase;
    font-size: 10px;
    opacity: 0.75;
    letter-spacing: 0.1rem;
    margin-bottom: 5px;
    color: white;
  }

  .upload-btn {
    width: 100%;
    margin-top: 10px;

    i,
    svg {
      margin-right: 7px;
    }
  }

  .right-lg {
    opacity: 0.75;
    position: relative;
    top: -8px;

    label {
      text-transform: none !important;
    }

    @media (min-width: $md-min) {
      text-align: right;
      top: 0;
    }
  }

  .add-file-btn {
    height: 45px;
    width: 100%;
    border-color: $green;
    background: none;
    color: $green;

    &:hover {
      border-color: $green;
      background: $green;
      color: #1c3a58;
    }
  }
}
</style>
